import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { FieldValues, Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { AsyncSearchableSelect } from "../../../components/Form/AsyncSearchableSelect/AsyncSearchableSelect.js";
import { SelectField } from "../../../components/Form/fields/SelectField.js";
import { TextAreaField } from "../../../components/Form/fields/TextAreaField.js";
import type { FormFieldConfig } from "../../../types/uiTypes.js";
import { TextField } from "../../../components/Form/fields/TextField.js";
import { FormSuccessScreen } from "../../../components/Form/FormSuccessScreen.js";
import { PageHeader } from "../../../components/PageHeader/PageHeader.js";
import { companies } from "../../../api/companies.service.js";
import { directories } from "../../../api/directories.service.js";
import { editions } from "../../../api/editions.service.js";
import { regions } from "../../../api/regions.service.js";
import { bpas } from "../../../api/bpas.service.js";
import { useQuery } from "../../../hooks/useApi.js";
import { ArrayField } from "./ArrayField.js";
import {
  commentaryEditFormSchema,
  commentaryFormSchema,
  commentaryFormDefaultValues,
  stripDirectoryPrefix,
  toOptionMap,
  type CommentaryFormValues,
  type TaxonomyLabelMaps,
  type TaxonomySelectionState,
} from "./commentaryForm.helpers.js";
import formStyles from "../../../components/Form/forms/GenericForm.module.css";
import styles from "./CreateCommentary.module.css";

type TextareaFieldConfig = Omit<FormFieldConfig, "label"> & { label: React.ReactNode };

type CommentaryFormProps = {
  mode: "create" | "edit";
  title: string;
  submitLabel: string;
  loadingLabel: string;
  initialValues?: CommentaryFormValues;
  initialSelectionState?: TaxonomySelectionState;
  statusActions?: React.ReactNode;
  readOnly?: boolean;
  saveRef?: React.RefObject<(() => Promise<void>) | null>;
  onSubmit: (
    values: CommentaryFormValues,
    selectionState: TaxonomySelectionState,
    labelMaps: TaxonomyLabelMaps,
  ) => Promise<void>;
  onCancel: () => void;
  onDone: () => void;
};

type TextareaItem = {
  kind: "textarea";
  name: keyof CommentaryFormValues;
  label: React.ReactNode;
  labelText?: string;
};

type ArrayItem = {
  kind: "array";
  name: keyof CommentaryFormValues;
  label: React.ReactNode;
  labelText?: string;
  max: number;
};

type SubHeadingItem = {
  kind: "subheading";
  heading: string;
};

type ContentItem = TextareaItem | ArrayItem | SubHeadingItem;

type ContentPage = {
  heading: string;
  items: ContentItem[];
};

const contentPages: ContentPage[] = [
  {
    heading: "Executive summary",
    items: [
      { kind: "textarea", name: "rankingTrend", label: "Ranking forecast" },
      { kind: "array", name: "headlineDifferentiators", label: "Key strengths", max: 4 },
      { kind: "array", name: "headlineChallenges", label: "Key challenges", max: 4 },
      { kind: "array", name: "kpis", label: "KPIs", max: 4 },
    ],
  },
  {
    heading: "Ranking trend analysis",
    items: [
      { kind: "textarea", name: "summary", label: "Summary" },
      {
        kind: "textarea",
        name: "legalMarketConditions",
        label: "Legal market and ranking trends",
      },
      {
        kind: "textarea",
        name: "externalMarketConditions",
        label: "External and economic market trends",
      },
      {
        kind: "textarea",
        name: "legislativePoliticalTrends",
        label: "Legislative and political trends",
      },
      { kind: "textarea", name: "crossPracticeOverview", label: "Cross-practice overview" },
      { kind: "textarea", name: "recommendedComparators", label: "Recommended comparators" },
      {
        kind: "textarea",
        name: "rankingDecisionMatrixDifferentiators",
        label: strengthsLabel("Ranking decision matrix"),
        labelText: "Ranking decision matrix – Strengths and differentiators",
      },
      {
        kind: "textarea",
        name: "rankingDecisionMatrixChallenges",
        label: challengesLabel("Ranking decision matrix"),
        labelText: "Ranking decision matrix – Challenges, weaknesses or risks",
      },
    ],
  },
  {
    heading: "Client service benchmark analysis",
    items: [
      { kind: "textarea", name: "market", label: "Market" },
      { kind: "textarea", name: "peerGroup", label: "Peer group" },
      { kind: "subheading", heading: "Capability" },
      {
        kind: "textarea",
        name: "capabilityDifferentiators",
        label: <span className={styles.labelStrengths}>Strengths and differentiators</span>,
        labelText: "Strengths and differentiators",
      },
      {
        kind: "textarea",
        name: "capabilityChallenges",
        label: <span className={styles.labelChallenges}>Challenges, weaknesses or risks</span>,
        labelText: "Challenges, weaknesses or risks",
      },
      { kind: "subheading", heading: "Client priorities – market" },
      { kind: "array", name: "clientPrioritiesHigh", label: "High priority", max: 10 },
      { kind: "array", name: "clientPrioritiesMedium", label: "Medium priority", max: 10 },
      { kind: "array", name: "clientPrioritiesLow", label: "Low priority", max: 10 },
      { kind: "subheading", heading: "Meeting client needs – firm" },
      { kind: "array", name: "meetingClientNeedsHigh", label: "High priority", max: 10 },
      { kind: "array", name: "meetingClientNeedsMedium", label: "Medium priority", max: 10 },
      { kind: "array", name: "meetingClientNeedsLow", label: "Low priority", max: 10 },
      { kind: "subheading", heading: "Best practice" },
      { kind: "textarea", name: "quotesPromoter", label: "Promoters (9, 10)" },
      { kind: "textarea", name: "quotesPassives", label: "Passives (7, 8)" },
      { kind: "textarea", name: "quotesDetractors", label: "Detractors (0–6)" },
    ],
  },
  {
    heading: "High performance modelling",
    items: [
      {
        kind: "array",
        name: "strategicRecommendations",
        label: "Strategic recommendation",
        labelText: "Strategic recommendation",
        max: 4,
      },
    ],
  },
];

function textField(name: string, label: string, placeholder?: string) {
  return { name, label, type: "text" as const, placeholder };
}

function textareaField(name: string, label: React.ReactNode): TextareaFieldConfig {
  return { name, label, type: "textarea" };
}

function strengthsLabel(prefix: string): React.ReactNode {
  return (
    <>
      {prefix} – <span className={styles.labelStrengths}>Strengths and differentiators</span>
    </>
  );
}

function challengesLabel(prefix: string): React.ReactNode {
  return (
    <>
      {prefix} – <span className={styles.labelChallenges}>Challenges, weaknesses or risks</span>
    </>
  );
}

function displayValue(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : "-";
}

function ReadOnlyTextField({
  id,
  label,
  value,
  testId,
}: {
  id: string;
  label: string;
  value: string;
  testId: string;
}) {
  return (
    <div className={formStyles.formField} data-testid={testId}>
      <label htmlFor={id} className={formStyles.label}>
        {label}
      </label>
      <Input id={id} value={value} disabled aria-readonly />
    </div>
  );
}

function getDefaultSelectionState(
  initialSelectionState: TaxonomySelectionState | undefined,
): TaxonomySelectionState {
  return {
    firmId: initialSelectionState?.firmId ?? null,
    firmName: initialSelectionState?.firmName ?? null,
    publicationName: initialSelectionState?.publicationName ?? null,
    editionName: initialSelectionState?.editionName ?? null,
    locationName: initialSelectionState?.locationName ?? null,
    practiceName: initialSelectionState?.practiceName ?? null,
  };
}

function CommentaryForm({
  mode,
  title,
  submitLabel,
  loadingLabel,
  initialValues,
  initialSelectionState,
  statusActions,
  readOnly,
  saveRef,
  onSubmit,
  onCancel,
  onDone,
}: CommentaryFormProps) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const isEditMode = mode === "edit";
  const isReadOnly = readOnly ?? false;
  const defaultSelectionState = useMemo(
    () => getDefaultSelectionState(initialSelectionState),
    [initialSelectionState],
  );

  const [editionOptions, setEditionOptions] = useState([
    { value: "", label: "Select publication first" },
  ]);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(
    defaultSelectionState.firmId,
  );
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(
    defaultSelectionState.firmName,
  );
  const [companyError, setCompanyError] = useState<string | null>(null);

  const editionRequestIdRef = useRef(0);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FieldValues>({
    resolver: zodResolver(
      isEditMode ? commentaryEditFormSchema : commentaryFormSchema,
    ) as unknown as Resolver<FieldValues>,
    mode: "onSubmit",
    defaultValues: initialValues ?? commentaryFormDefaultValues,
  });

  useEffect(() => {
    reset(initialValues ?? commentaryFormDefaultValues);
    setSelectedCompany(defaultSelectionState.firmId);
    setSelectedCompanyName(defaultSelectionState.firmName);
    setCompanyError(null);
    setEditionOptions([{ value: "", label: "Select publication first" }]);
  }, [initialValues, defaultSelectionState, reset]);

  const searchCompanies = useCallback(async (query: string) => {
    const results = await companies.search(query);
    return results.map(company => ({
      value: String(company.id),
      label: company.name,
      description: company.crmEntityNumber ?? undefined,
    }));
  }, []);

  const { data: directoriesData } = useQuery(
    () => (isEditMode ? Promise.resolve([]) : directories.getAll()),
    [isEditMode],
  );

  const { data: regionsData } = useQuery(
    () => (isEditMode ? Promise.resolve([]) : regions.getAll()),
    [isEditMode],
  );

  const { data: bpasData } = useQuery(
    () => (isEditMode ? Promise.resolve([]) : bpas.getAll()),
    [isEditMode],
  );

  const publicationOptions = useMemo(
    () => [
      { value: "", label: "Select publication" },
      ...(directoriesData ?? [])
        .filter(d => d.parentId === null)
        .map(d => ({ value: d.id, label: d.name as string })),
    ],
    [directoriesData],
  );

  const regionOptions = useMemo(
    () => [
      { value: "", label: "Select region" },
      ...(regionsData ?? []).map(r => ({ value: r.id, label: r.name })),
    ],
    [regionsData],
  );

  const practiceOptions = useMemo(
    () => [
      { value: "", label: "Select practice" },
      ...(bpasData ?? []).map(b => ({ value: b.id, label: b.name })),
    ],
    [bpasData],
  );

  const publicationOptionsById = useMemo(
    () => toOptionMap(publicationOptions),
    [publicationOptions],
  );
  const editionOptionsById = useMemo(() => toOptionMap(editionOptions), [editionOptions]);
  const regionOptionsById = useMemo(() => toOptionMap(regionOptions), [regionOptions]);
  const practiceOptionsById = useMemo(() => toOptionMap(practiceOptions), [practiceOptions]);

  useEffect(() => {
    if (saveRef) {
      saveRef.current = handleSubmit(async values => {
        await onSubmit(
          values as CommentaryFormValues,
          {
            firmId: selectedCompany,
            firmName: selectedCompanyName,
          },
          {
            publicationById: publicationOptionsById,
            editionById: editionOptionsById,
            locationById: regionOptionsById,
            businessPracticeAreaById: practiceOptionsById,
          },
        );
      });
    }
  }, [
    saveRef,
    handleSubmit,
    onSubmit,
    selectedCompany,
    selectedCompanyName,
    publicationOptionsById,
    editionOptionsById,
    regionOptionsById,
    practiceOptionsById,
  ]);

  const watchedPublication = watch("publicationRefId") as string;
  const watchedEdition = watch("editionRefId") as string;

  const publicationName = useMemo(() => {
    const pubId = watchedPublication;
    if (!pubId) return null;
    return publicationOptionsById.get(pubId) ?? null;
  }, [watchedPublication, publicationOptionsById]);

  const editionDisplayOptions = useMemo(
    () =>
      editionOptions.map(option => ({
        ...option,
        label: option.value ? stripDirectoryPrefix(option.label, publicationName) : option.label,
      })),
    [editionOptions, publicationName],
  );

  useEffect(() => {
    if (isEditMode || !watchedPublication) {
      setEditionOptions([{ value: "", label: "Select publication first" }]);
      setValue("editionRefId", "");
      return;
    }

    const currentRequestId = ++editionRequestIdRef.current;

    const load = async () => {
      try {
        const result = await editions.getAll(watchedPublication);
        if (currentRequestId !== editionRequestIdRef.current) return;
        const options = result.map(e => ({ value: e.id, label: e.name as string }));
        setEditionOptions([{ value: "", label: "Select edition" }, ...options]);
      } catch (error) {
        if (currentRequestId !== editionRequestIdRef.current) return;
        console.error("Failed to load edition options", error);
        setEditionOptions([{ value: "", label: "Failed to load editions" }]);
      }
    };

    load();
  }, [isEditMode, watchedPublication, setValue]);

  const handleFormSubmit = async (data: FieldValues) => {
    if (!isEditMode && !selectedCompany) {
      setCompanyError("Firm is required");
      return;
    }

    setCompanyError(null);
    setSubmitting(true);

    try {
      await onSubmit(
        data as CommentaryFormValues,
        {
          firmId: selectedCompany,
          firmName: selectedCompanyName,
        },
        {
          publicationById: publicationOptionsById,
          editionById: editionOptionsById,
          locationById: regionOptionsById,
          businessPracticeAreaById: practiceOptionsById,
        },
      );

      setShowSuccess(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setToast({ message, type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAnother = () => {
    reset(commentaryFormDefaultValues);
    setSelectedCompany(null);
    setSelectedCompanyName(null);
    setCompanyError(null);
    setToast(null);
    setEditionOptions([{ value: "", label: "Select publication first" }]);
    setShowSuccess(false);
  };

  if (showSuccess) {
    return (
      <div className={styles.wrapper}>
        <FormSuccessScreen
          mode={mode}
          singularName="Commentary"
          onClose={onDone}
          onCreateAnother={mode === "create" ? handleCreateAnother : undefined}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader title={title} />
      <form onSubmit={handleSubmit(handleFormSubmit)} className={formStyles.form}>
        <section className={formStyles.formSection}>
          {isEditMode ? (
            <>
              <div className={styles.taxonomyRow}>
                <div className={`${styles.filterDropdown} ${styles.filterDropdownWide}`}>
                  <ReadOnlyTextField
                    id="readonly-publication"
                    label="Publication"
                    value={displayValue(defaultSelectionState.publicationName)}
                    testId="readonly-publication"
                  />
                </div>
                <div className={styles.filterDropdown}>
                  <ReadOnlyTextField
                    id="readonly-edition"
                    label="Edition"
                    value={displayValue(
                      stripDirectoryPrefix(
                        defaultSelectionState.editionName,
                        defaultSelectionState.publicationName,
                      ),
                    )}
                    testId="readonly-edition"
                  />
                </div>
                <div className={`${styles.filterDropdown} ${styles.filterDropdownWide}`}>
                  <ReadOnlyTextField
                    id="readonly-practice"
                    label="Practice"
                    value={displayValue(defaultSelectionState.practiceName)}
                    testId="readonly-practice"
                  />
                </div>
                <div className={styles.filterDropdown}>
                  <ReadOnlyTextField
                    id="readonly-region"
                    label="Region"
                    value={displayValue(defaultSelectionState.locationName)}
                    testId="readonly-region"
                  />
                </div>
              </div>

              <div className={styles.taxonomyRow}>
                <div className={styles.filterDropdown}>
                  <ReadOnlyTextField
                    id="readonly-firm"
                    label="Firm"
                    value={displayValue(selectedCompanyName)}
                    testId="readonly-firm"
                  />
                </div>
                <div className={styles.filterDropdown}>
                  <ReadOnlyTextField
                    id="readonly-assignee"
                    label="Assignee Email"
                    value={displayValue(watch("assignee") as string)}
                    testId="readonly-assignee"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.taxonomyRow}>
                <div className={`${styles.filterDropdown} ${styles.filterDropdownWide}`}>
                  <SelectField
                    field={{
                      name: "publicationRefId",
                      label: "Publication",
                      type: "select",
                      options: publicationOptions,
                      required: true,
                    }}
                    control={control}
                    errors={errors}
                  />
                </div>
                <div className={styles.filterDropdown}>
                  <SelectField
                    field={{
                      name: "editionRefId",
                      label: "Edition",
                      type: "select",
                      options: editionDisplayOptions,
                      disabled: !watchedPublication,
                      required: true,
                    }}
                    control={control}
                    errors={errors}
                  />
                </div>
                <div className={`${styles.filterDropdown} ${styles.filterDropdownWide}`}>
                  <SelectField
                    field={{
                      name: "bpaRefId",
                      label: "Practice",
                      type: "select",
                      options: practiceOptions,
                      disabled: !watchedEdition,
                      required: true,
                    }}
                    control={control}
                    errors={errors}
                  />
                </div>
                <div className={styles.filterDropdown}>
                  <SelectField
                    field={{
                      name: "locationRefId",
                      label: "Region",
                      type: "select",
                      options: regionOptions,
                    }}
                    control={control}
                    errors={errors}
                  />
                </div>
              </div>

              <div className={styles.taxonomyRow}>
                <div className={styles.filterDropdown}>
                  <AsyncSearchableSelect
                    name="firmSearch"
                    label="Firm"
                    required
                    value={selectedCompany}
                    onSearch={searchCompanies}
                    onChange={value => {
                      setSelectedCompany(value ? String(value) : null);
                      if (!value) {
                        setSelectedCompanyName(null);
                      }
                      if (value) setCompanyError(null);
                    }}
                    onOptionChange={option => {
                      setSelectedCompanyName(option?.label ?? null);
                    }}
                    placeholder="Search firm name..."
                    minSearchLength={2}
                    debounceMs={300}
                    error={companyError ?? undefined}
                    initialOption={
                      selectedCompany && selectedCompanyName
                        ? { value: selectedCompany, label: selectedCompanyName }
                        : null
                    }
                  />
                </div>
                <div className={styles.filterDropdown}>
                  <TextField
                    field={{
                      ...textField("assignee", "Assignee Email", "Enter assignee email"),
                      required: true,
                    }}
                    control={control}
                    errors={errors}
                  />
                </div>
              </div>
            </>
          )}

          {isEditMode &&
            contentPages.map(page => (
              <section key={page.heading} className={styles.contentPage}>
                <h3 className={styles.contentPageHeading}>{page.heading}</h3>
                {page.items.map(item => {
                  if (item.kind === "subheading") {
                    return (
                      <h4 key={item.heading} className={styles.contentPageSubHeading}>
                        {item.heading}
                      </h4>
                    );
                  }

                  if (item.kind === "textarea") {
                    return (
                      <TextAreaField
                        key={item.name}
                        field={
                          {
                            ...textareaField(item.name, item.label),
                            disabled: isReadOnly,
                          } as unknown as FormFieldConfig
                        }
                        control={control}
                        errors={errors}
                      />
                    );
                  }

                  return (
                    <ArrayField
                      key={item.name}
                      name={item.name}
                      label={item.label}
                      labelText={item.labelText}
                      max={item.max}
                      control={control}
                      errors={errors}
                      disabled={isReadOnly}
                    />
                  );
                })}
              </section>
            ))}
        </section>

        <div
          className={`${formStyles.formActions} ${styles.formActions} ${!isEditMode ? styles.formActionsNoDivider : ""}`}
        >
          {statusActions && (
            <div style={{ display: "flex", gap: "var(--spacing-3)", marginRight: "auto" }}>
              {statusActions}
            </div>
          )}
          <Button type="button" onClick={onCancel} size="medium" variant="outlined">
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              type="submit"
              disabled={submitting}
              size="medium"
              variant="contained"
              color="primary"
              aria-label={submitLabel}
            >
              {submitting ? loadingLabel : submitLabel}
            </Button>
          )}
        </div>
      </form>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default CommentaryForm;
